import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import axiosClient from '../../api/axiosClient';
import { Book, ChevronRight, Search } from 'lucide-react';

const PublicDocsPage = () => {
  const [selectedSlug, setSelectedSlug] = useState(null);
  
  // Fetch All Docs
  const { data: docs = [], isLoading } = useQuery({
    queryKey: ['publicDocs'],
    queryFn: async () => {
      const res = await axiosClient.get('/public/docs');

      const actualData =
        res.data !== undefined ? res.data : res;

      return Array.isArray(actualData) ? actualData : [];
    },
  });

  useEffect(() => {
  const handleCopy = async (e) => {
    const button = e.target.closest('[data-copy-command]');

    if (!button) return;

    const command = button.getAttribute('data-copy-command');

    try {
      await navigator.clipboard.writeText(command);

      const original = button.innerHTML;

      button.innerHTML = '✓ Copied';

      button.classList.remove('text-cyan-400');
      button.classList.add('text-emerald-400');

      setTimeout(() => {
        button.innerHTML = original;

        button.classList.remove('text-emerald-400');
        button.classList.add('text-cyan-400');
      }, 2000);

    } catch (err) {
      button.innerHTML = '✗ Failed';

      setTimeout(() => {
        button.innerHTML = 'Copy';
      }, 2000);
    }
  };

  document.addEventListener('click', handleCopy);

  return () => {
    document.removeEventListener('click', handleCopy);
  };
}, []);


  // Group by Category
  const grouped = docs.reduce((acc, doc) => {
    (acc[doc.category] = acc[doc.category] || []).push(doc);
    return acc;
  }, {});

  // Select first doc by default
  const activeDoc = selectedSlug 
    ? docs.find(d => d.slug === selectedSlug) 
    : (docs.length > 0 ? docs[0] : null);

  if (isLoading) return <div className="min-h-screen bg-[#020617] pt-32 text-center text-slate-500">Loading Knowledge Base...</div>;

  return (
    <div className="min-h-screen bg-[#020617] pt-20">
      <div className="max-w-7xl mx-auto px-4 flex flex-col md:flex-row gap-8">
        
        {/* SIDEBAR */}
        <div className="w-full md:w-72 flex-shrink-0 py-8">
          <h2 className="text-white font-bold text-xl mb-6 flex items-center">
            <Book className="w-5 h-5 mr-2 text-teal-500"/> Documentation
          </h2>
          
          <div className="space-y-6">
            {Object.entries(grouped).map(([category, items]) => (
              <div key={category}>
                <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">{category}</h3>
                <div className="space-y-1">
                  {items.map(doc => (
                    <button
                      key={doc.id}
                      onClick={() => setSelectedSlug(doc.slug)}
                      className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors flex items-center ${
                        activeDoc?.id === doc.id 
                          ? 'bg-blue-600/20 text-teal-400 border border-blue-600/30' 
                          : 'text-slate-400 hover:text-slate-200 hover:bg-white/5'
                      }`}
                    >
                      {activeDoc?.id === doc.id && <ChevronRight className="w-3 h-3 mr-2"/>}
                      {doc.title}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* MAIN CONTENT */}
        <div className="flex-1 py-8 md:pl-8 md:border-l border-slate-800 min-h-screen">
          {activeDoc ? (
            <div className="animate-in fade-in slide-in-from-bottom-4">
              <h1 className="text-3xl md:text-4xl font-black text-white mb-6">{activeDoc.title}</h1>
              <div 
                className="prose prose-lg prose-invert max-w-none 
                  prose-headings:text-white prose-p:text-slate-300 prose-a:text-teal-400 
                  prose-code:bg-slate-900 prose-code:text-emerald-400 prose-code:px-1 prose-code:rounded
                  prose-pre:bg-[#0b0f19] prose-pre:border prose-pre:border-slate-800"
                dangerouslySetInnerHTML={{ __html: activeDoc.content_html }}
              />
            </div>
          ) : (
            <div className="text-center py-20 text-slate-500">
              <Search className="w-12 h-12 mx-auto mb-4 opacity-20"/>
              <p>Select a topic to begin.</p>
            </div>
          )}
        </div>

      </div>
    </div>
  );
};

export default PublicDocsPage;